<script setup lang="ts">
import { helpers } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'

import { Form, Pet } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Pet
  argentoInsuranceCompanyId?: number | null
  argentoInsuranceProductId?: number | null
  argentoInsurancebrokerProductId?: number | null
}

const props = defineProps<Props>()
const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () => ({
    petType: { required: requiredValidator },
    name: { required: requiredValidator },
    fertility: { required: requiredValidator },
    color: { selected: helpers.withMessage('Seleccione un color.', () => !!model.value.color?.id) },
    breed: { selected: helpers.withMessage('Seleccione una raza.', () => !!model.value.breed?.id) },
    birthDate: { required: requiredValidator },
    size: { required: requiredValidator },
    gender: { required: requiredValidator },
  }),
  model,
  { $scope: Form.pet, $registerAs: Form.pet }
)

const { builder: dispatcherParamsBuilder } = useSiteApiDispatcherByProductParams({
  argentoInsuranceProps: computed(() => ({
    emisorId: props.argentoInsuranceCompanyId,
    brokerProductoId: props.argentoInsurancebrokerProductId,
    productoId: props.argentoInsuranceProductId,
  })),
})

const {
  execute: fetchColors,
  data: colors,
  status: colorsStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/AnimalColores',
    options: { immediate: true },
  })
)

const {
  execute: fetchBreeds,
  data: breeds,
  status: breedsStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/AnimalRazas',
    options: { immediate: true },
  })
)

watch(
  () => model.value.color?.id,
  (n, o) => {
    if (!n || n === o) return
    const color = colors.value?.items?.find((item) => item.id === n)
    if (color?.id) model.value.color = { id: color?.id, code: color?.codigo, name: color?.nombre }
  }
)

watch(
  () => model.value.breed?.id,
  (n, o) => {
    if (!n || n === o) return
    const breed = breeds.value?.items?.find((item) => item.id === n)
    if (breed?.id) model.value.breed = { id: breed?.id, code: breed?.codigo, name: breed?.nombre }
  }
)
</script>

<template>
  <FormsGlobalContainer :identifier="Form.pet">
    <FormsGlobalTitle
      text="Información de la mascota"
      icon-class="icon-[ph--dog-bold]"
    />

    <InputField
      label="Nombre"
      :error="v$.name?.$error"
      :error-message="v$.name?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.name" />
    </InputField>

    <InputField label="Número de microchip">
      <PvInputText v-model="model.microchip" />
    </InputField>

    <InputField
      label="Raza"
      :error="v$.breed?.$error"
      :error-message="v$.breed?.$errors[0]?.$message.toString()"
      :loading="breedsStatus === 'pending'"
    >
      <InputGroupRetry
        :retry="!breeds?.items?.length && !!model.breed?.id"
        @retry="fetchBreeds()"
      >
        <PvSelect
          v-if="model.breed"
          v-model="model.breed.id"
          option-value="id"
          option-label="nombre"
          :options="breeds?.items ?? []"
        />
      </InputGroupRetry>
    </InputField>

    <InputField
      label="Color"
      :error="v$.color?.$error"
      :error-message="v$.color?.$errors[0]?.$message.toString()"
      :loading="colorsStatus === 'pending'"
    >
      <InputGroupRetry
        :retry="!colors?.items?.length && !!model.color?.id"
        @retry="fetchColors()"
      >
        <PvSelect
          v-if="model.color"
          v-model="model.color.id"
          :options="colors?.items ?? []"
          option-value="id"
          option-label="nombre"
        />
      </InputGroupRetry>
    </InputField>

    <InputField
      label="Tamaño"
      :error="v$.size?.$error"
      :error-message="v$.size?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.size"
        option-label="label"
        option-value="code"
        :options="Pet.getSizeTypeList()"
      />
    </InputField>

    <InputField
      label="Fecha de nacimiento"
      :error="v$.birthDate?.$error"
      :error-message="v$.birthDate?.$errors[0]?.$message.toString()"
    >
      <PvDatePicker
        v-model="model.birthDate"
        :max-date="new Date()"
      />
    </InputField>

    <InputField
      label="Aparato reproductivo"
      :error="v$.fertility?.$error"
      :error-message="v$.fertility?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.fertility"
        option-label="label"
        option-value="code"
        :options="Pet.getFertilityTypeList()"
      />
    </InputField>

    <InputField
      label="Sexo"
      :error="v$.gender?.$error"
      :error-message="v$.gender?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.gender"
        option-label="label"
        option-value="code"
        :options="Pet.getPetGenderTypeList()"
      />
    </InputField>
  </FormsGlobalContainer>
</template>
